import { Clipboard } from '@angular/cdk/clipboard';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, ElementRef, HostListener, OnInit, ViewChild } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { PendoService } from 'analytics-lib';
import { BullhornAuthService } from 'auth-service-lib';
import { jwtDecode } from 'jwt-decode';
import { ILlm, LlmConfigService } from 'llm-config';
import { MessageNotificationService } from 'message-notification-service-lib';
import { forkJoin, Observable } from 'rxjs';
import { concatMap, tap } from 'rxjs/operators';
import { IdentityStorage } from 'storage-service-lib';
import { environment } from '../../../environments/environment';
import { BullhornMetaService, IEntityLabelPair } from '../../services/bullhorn-meta.service';
import { BullhornIconService } from '../../services/bullhorn.icon.service';
import { ConfigService } from '../../services/config.service';
import { PromptProfileService } from '../../services/prompt-profile.service';
import { AIApps } from '../../types/AIApps';
import { BullhornEntityType } from '../../types/bullhorn/bullhorn-entity-type';
import { IClientConfig } from '../../types/client-config';
import { IAwesomeDocsConfig } from '../../types/IAwesomeDocs';
import { IDataToolsConfig } from '../../types/IDataTools';
import { IPromptOptions, IPromptProfileMeta } from '../../types/IPromptProfileMeta';
import { IEntityTypeCount, IPromptProfile } from '../../types/prompt-profile';
import { DialogConfirmComponent } from '../dialog-confirm/dialog-confirm.component';
import { AssistManagerAdminComponent } from './assist-manager-admin/assist-manager-admin.component';
import { IDialogConfirm, PromptProfileComponent } from './prompt-profile/prompt-profile.component';

@Component({
  selector: 'app-assist-manager-landing',
  templateUrl: './assist-manager-landing.component.html',
  styleUrls: ['./assist-manager-landing.component.scss'],
  standalone: false,
})
export class AssistManagerLandingComponent implements OnInit {
  private indexOfOpenPromptProfile = -1;
  protected readonly appVersion;
  protected expandVersion = false;
  protected readonly environment = environment;

  MAX_COLUMNS = 5;
  loading = true;
  noResults = false;
  noKSPResults = false;
  reOrdered = false;
  clientConfig!: IClientConfig;
  DTConfig!: IDataToolsConfig;
  ADConfig!: IAwesomeDocsConfig;
  LLMList: ILlm[];
  promptOptions!: IPromptOptions;

  clientTableDisplayedColumns: string[] = [
    'entityType',
    'displayName',
    'action',
    'integration',
    'options',
  ];
  kyloeTableDisplayedColumns: string[] = [
    'entityType',
    'displayName',
    'action',
    'role',
    'tone',
    'format',
    'resultLength',
    'explanationDifficulty',
    'outputLanguage',
    'options',
  ];
  entityTypesUsed: IEntityTypeCount[] = [];
  currentEntityFilter?: BullhornEntityType;
  searchFilter = '';

  // Prompts Tables
  clientPromptsDataSource: MatTableDataSource<IPromptProfile> =
    new MatTableDataSource<IPromptProfile>();
  kyloePromptsDataSource: MatTableDataSource<IPromptProfile> =
    new MatTableDataSource<IPromptProfile>();
  @ViewChild('clientPromptsTableSort', { static: false }) clientPromptsTableSort!: MatSort;
  @ViewChild('kyloePromptsTableSort', { static: false }) kyloePromptsTableSort!: MatSort;

  entities: IEntityLabelPair[] = [];

  entityKey = 'entityMeta';

  DTAIEnabled = false;
  ADAIEnabled = false;

  integrationMappings: { [key: string]: string } = {
    datarules: 'Kyloe DataTools',
    awesomedocs: 'Kyloe AwesomeDocs',
  };

  @ViewChild('searchInput') searchInput!: ElementRef;
  @ViewChild('clearSearch') clearSearch!: ElementRef;
  constructor(
    public router: Router,
    protected bullhornIconService: BullhornIconService,
    private clientConfigService: ConfigService,
    private promptProfileService: PromptProfileService,
    private llmConfigService: LlmConfigService,
    private identityStorage: IdentityStorage,
    private messageNotificationService: MessageNotificationService,
    private dialog: MatDialog,
    private bullhornAuthService: BullhornAuthService,
    private clipboard: Clipboard,
    private bullhornMetaService: BullhornMetaService,
    private pendoService: PendoService
  ) {
    this.appVersion = environment.version;
  }

  backHome() {
    this.router.navigate(['']);
  }

  ngOnInit(): void {
    this.load();
    this.loadPendo();
  }

  private loadPendo() {
    this.pendoService.initPendoScript(environment.aiaManagerPendoAPIKey);
  }

  drop(event: CdkDragDrop<Element[]>) {
    moveItemInArray(this.clientPromptsDataSource.data, event.previousIndex, event.currentIndex);
    this.clientPromptsDataSource.data = [...this.clientPromptsDataSource.data];
    this.reOrdered = true;
  }

  showModal(
    row: IPromptProfile = {
      corpId: this.clientConfig.corpId,
      action: undefined,
      role: undefined,
      tone: undefined,
      resultLength: undefined,
      format: undefined,
      explanationDifficulty: undefined,
      outputLanguage: undefined,
      outputFieldName: undefined,
      removeBias: true,
      anonymise: false,
      enableCopyButton: true,
      entityType: this.currentEntityFilter ?? BullhornEntityType.CANDIDATE,
      canCustomize: true,
      enabled: false,
      displayName: '',
      LLMs: '',
      sourceFields: '',
    }
  ): void {
    const promptProfileDialog = this.dialog.open(PromptProfileComponent, {
      maxHeight: '93vh',
      minWidth: '56vw',
      autoFocus: true,
      data: {
        promptProfile: row,
        promptOptions: this.promptOptions,
        entities: this.entities,
        DTAIEnabled: this.DTAIEnabled,
        ADAIEnabled: this.ADAIEnabled,
        isDataRulesVisible: this.DTConfig.datarules ? this.DTConfig.datarules : false,
        LLmList: this.LLMList,
      },
      disableClose: true,
    });
    this.indexOfOpenPromptProfile = this.clientPromptsDataSource.data.indexOf(row);
    promptProfileDialog.backdropClick().subscribe((_) => {
      promptProfileDialog.close();
    });

    const $closeEvent = promptProfileDialog.afterClosed() as Observable<IModalCloseEventData>;

    $closeEvent.subscribe((closeEventData) => {
      if (!closeEventData) {
        return;
      }
      if (closeEventData.action === 'save') {
        const hasEntityChanged = row.entityType !== closeEventData.promptProfile.entityType;
        this.savePrompt({
          prompt: closeEventData.promptProfile,
          hasEntityChanged: hasEntityChanged,
        }).subscribe((result) => {
          this.messageNotificationService.success(
            `${closeEventData.promptProfile.displayName} saved`
          );
          if (hasEntityChanged) {
            this.deletePrompt(row, hasEntityChanged);
          }
          return result;
        });
      } else if (closeEventData.action === 'delete') {
        this.deletePrompt(closeEventData.promptProfile);
      }
    });
  }

  showAdminDialog(): void {
    const adminDialog = this.dialog.open(AssistManagerAdminComponent, {
      minWidth: '50vw',
      minHeight: '20vw',
      maxHeight: '83vh',
      autoFocus: true,
      disableClose: true,
      data: {
        options: JSON.parse(JSON.stringify(this.promptOptions)),
        baseHtmlStyleFile: {
          name: this.clientConfig.promptProfileMeta?.baseHtmlStyleFile?.name,
          file: undefined,
        },
      },
    });

    adminDialog.afterClosed().subscribe((result: IPromptProfileMeta) => {
      if (result) {
        this.promptOptions = result.options;
        this.clientConfig.promptProfileMeta = result;
        this.clientConfigService.save(this.clientConfig).subscribe((configResult) => {
          this.messageNotificationService.success('Global prompt options saved successfully.');
        });
      }
    });

    adminDialog.backdropClick().subscribe((_) => {
      adminDialog.close();
    });
  }

  saveProfiles(): void {
    const promptOrder = this.updateProfileOrder();
    this.clientConfig.promptProfileOrder = {
      order: promptOrder,
    };
    this.clientConfigService.save(this.clientConfig).subscribe({
      next: (clientConfig) => {
        this.messageNotificationService.success('Changes Saved');
        this.entityTypesUsed = [];
        this.currentEntityFilter = undefined;
        this.populateClientPromptsDatasource(this.clientPromptsDataSource.data);
      },
      error: (error) => {
        this.messageNotificationService.error('Failed to save', error);
      },
    });
    this.reOrdered = false;
  }

  clearInput(inputElement: HTMLElement, focusInput?: boolean): void {
    if (this.dialog.openDialogs.length === 0) {
      this.clientPromptsDataSource.filter = ''; // Reset the filter
      this.kyloePromptsDataSource.filter = '';
      this.searchFilter = '';
      this.currentEntityFilter = undefined;
      this.noResults = false;
      this.noKSPResults = false;

      const input = inputElement as HTMLInputElement;
      input.value = '';
      if (focusInput) { inputElement.focus(); }
    }
  }

  private load(): void {
    if (!this.identityStorage.get()) {
      this.bullhornAuthService.getToken().subscribe(() => {
        this.load();
      });
      return;
    }

    // ToDO refactor this whole method into a new method in getToken().subscribe
    const storedJson = this.identityStorage.get()!;
    const jwt = jwtDecode<any>(storedJson.token);

    const corpId = jwt.corporationId;
    const namespaceId = `bullhorn-${corpId}`;

    const configSources = [
      this.clientConfigService.getFullConfigForCorpId(
        AIApps.ASSIST,
        environment.apiGatewayUrl,
        environment.dtApiGatewayUrl,
        namespaceId
      ),
      this.clientConfigService.getDatatoolsConfigForCorpId(corpId),
      this.clientConfigService.getAwesomeDocsConfigForCorpId(namespaceId),
    ];

    forkJoin(configSources).pipe(
      concatMap((response) => {
        this.clientConfig = response[0] as IClientConfig;
        this.DTConfig = response[1] as IDataToolsConfig;
        this.ADConfig = response[2] as IAwesomeDocsConfig;
        return this.llmConfigService.getLlmModels(this.clientConfig.enabledModels ?? []);
      })
    ).subscribe({
      next: (llmList) => {
        this.LLMList = llmList;
        this.promptOptions = this.clientConfig.promptProfileMeta?.options as IPromptOptions;
        if (!this.clientConfig.enabled) {
          this.messageNotificationService.error('Client is not enabled'); // TODO
          return;
        }

        if (this.DTConfig) {
          this.DTAIEnabled = this.setDTAIEnabled(this.DTConfig);
        } else {
          this.DTAIEnabled = false;
        }
        this.clientConfig.DTAIEnabled = this.DTAIEnabled;

        if (this.ADConfig) {
          this.ADAIEnabled = this.setADAIEnabled(this.ADConfig);
        } else {
          this.ADAIEnabled = false;
        }
        this.clientConfig.ADAIEnabled = this.ADAIEnabled;

        this.bullhornMetaService.getAllEntityMeta().subscribe({
          next: () => {
            this.entities = this.bullhornMetaService.getLabelPairs();
            this.updatePromptProfiles();
            this.loading = false;
          },
        });
      },
      error: (e) => {
        console.error('Error occurred:', e);
        this.messageNotificationService.error('Failed to load data.', e);
      },
    });
  }

  public getEntityLabel(entityType: BullhornEntityType): string {
    const entity = this.entities.find((entity) => entity.objectName === entityType);
    return entity ? entity.label : entityType;
  }

  private setDTAIEnabled(DTConfig: IDataToolsConfig) {
    return DTConfig.datarules && DTConfig.datarulesAIAssist;
  }

  private setADAIEnabled(ADConfig: IAwesomeDocsConfig): boolean {
    return ADConfig.aiAssistEnabled ?? false;
  }

  private updatePromptProfiles() {
    const obs$: Observable<any>[] = [
      this.clientConfigService.getPromptsForCorp(this.clientConfig),
      this.clientConfigService.getDefaultPrompts(),
    ];
    forkJoin(obs$).subscribe((result) => {
      const clientPrompts = result[0];
      const kyloePrompts = result[1];
      if (clientPrompts.length > 0) {
        this.verifyProfileOrder(clientPrompts);
        this.populateClientPromptsDatasource(clientPrompts);
      }

      if (kyloePrompts.length) {
        this.populateKyloePromptsDatasource(kyloePrompts);
      }
    });
  }

  delete(prompt: IPromptProfile, event: any): void {
    event.stopPropagation();
    const displayName = prompt.displayName;
    const integration = prompt.integration;

    const messages: string[] = [`Are you sure you want to delete ${displayName}?`];
    if (integration === 'awesomedocs') {
      messages.push('Any Awesomedocs template using this prompt will no longer work.');
    }

    const dialogDelete: MatDialogRef<DialogConfirmComponent> = this.dialog.open(
      DialogConfirmComponent,
      {
        width: '45vw',
        height: 'auto',
        autoFocus: true,
        data: {
          title: `Delete: ${displayName}`,
          message: messages,
          positiveText: 'Yes',
          negativeText: 'No',
        } as IDialogConfirm,
      }
    );

    dialogDelete.afterClosed().subscribe((takeAction: boolean) => {
      if (!takeAction) { return; }
      this.deletePrompt(prompt);
    });
  }

  // ToDo error messaging for UAT testing only
  deletePrompt(prompt: IPromptProfile, hideMessage?: boolean) {
    this.clientConfigService.deletePrompt(this.clientConfig, prompt).subscribe({
      next: (result) => {
        if (!hideMessage) {
          this.messageNotificationService.success(`${prompt.displayName} deleted`);
        }
        this.populateClientPromptsDatasource(result);
        return result;
      },
      error: (error) => {
        this.messageNotificationService.error(`Error deleting ${prompt.displayName}`, error);
      },
    });
  }

  private duplicatePrompt(prompt: IPromptProfile, event: any, isClient: boolean) {
    event.stopPropagation();
    const newPrompt = JSON.parse(JSON.stringify(prompt));
    newPrompt.displayName = isClient
      ? `COPY OF ${newPrompt.displayName}`
      : `${newPrompt.displayName}`;
    newPrompt.promptId = '';
    this.indexOfOpenPromptProfile = -1;
    const saveArgs = isClient ? { prompt: newPrompt, copyOfPrompt: prompt } : { prompt: newPrompt };
    this.savePrompt(saveArgs).subscribe((_) => {
      const successMessage = isClient
        ? `Copy of ${prompt.displayName} saved`
        : `${prompt.displayName} added to your prompt profiles`;
      this.messageNotificationService.success(successMessage);
    });
  }

  duplicateClientPrompt(prompt: IPromptProfile, event: any) {
    this.duplicatePrompt(prompt, event, true);
  }

  duplicateKyloePrompt(prompt: IPromptProfile, event: any) {
    this.duplicatePrompt(prompt, event, false);
  }

  private savePrompt({ prompt, copyOfPrompt, hasEntityChanged }: ISavePromptArgs): Observable<any> {
    const oldPromptId = prompt.promptId;
    prompt = this.formatPromptForDynamo(prompt, hasEntityChanged);

    return this.clientConfigService.savePrompt(this.clientConfig, prompt).pipe(
      tap((result) => {
        if (this.indexOfOpenPromptProfile < 0) {
          let promptOrder = this.updateProfileOrder();
          if (copyOfPrompt) {
            promptOrder = this.addPromptToOrder(promptOrder, prompt, copyOfPrompt!);
          } else {
            promptOrder = this.moveToTop(promptOrder, prompt);
          }
          this.clientConfig.promptProfileOrder = {
            order: promptOrder,
          };
          console.log(promptOrder);
          this.clientConfigService.save(this.clientConfig).subscribe();
        } else if (hasEntityChanged && oldPromptId !== prompt.promptId) {
          const promptOrder = this.updateProfileOrder();
          const oldPromptIndex = promptOrder.indexOf(oldPromptId);
          if (oldPromptIndex !== -1) {
            promptOrder[oldPromptIndex] = prompt.promptId;
            this.clientConfig.promptProfileOrder = {
              order: promptOrder,
            };
            console.log(promptOrder);
            this.clientConfigService.save(this.clientConfig).subscribe();
          }
        }
        this.populateClientPromptsDatasource(result);
      })
    );
  }

  /**
   * Sorts the array of prompt profiles according to the order specified in the client config.
   *
   * @param data An array of prompt profiles to be sorted.
   * @returns The sorted array of prompt profiles.
   */
  private sortProfileOrder(data: IPromptProfile[]): IPromptProfile[] {
    let orderedData = [];
    if (this.clientConfig.promptProfileOrder) {
      const promptIdsOrder: string[] = this.clientConfig.promptProfileOrder.order;
      // Find matching promptProfile from the order list
      for (const promptId of promptIdsOrder) {
        const matchingObject = data.find((obj) => obj.promptId === promptId);

        if (matchingObject) {
          orderedData.push(matchingObject);
        }
      }
    } else {
      orderedData = data;
    }
    return orderedData;
  }

  private updateProfileOrder(): any {
    const promptOrder: string[] = [];
    this.clientPromptsDataSource.data.forEach((prompt) => {
      promptOrder.push(prompt.promptId!);
    });
    return promptOrder;
  }

  private verifyProfileOrder(data: IPromptProfile[]): void {
    if (!this.clientConfig.promptProfileOrder) {
      this.clientConfig.promptProfileOrder = { order: [] };
    }
    let reSave = false;
    const databaseIds: string[] = [];
    data.forEach((prompt: IPromptProfile) => {
      if (prompt.promptId) {
        databaseIds.push(prompt.promptId);
        if (!this.clientConfig.promptProfileOrder.order.includes(prompt.promptId)) {
          this.clientConfig.promptProfileOrder.order.push(prompt.promptId);
          reSave = true;
        }
      }
    });
    const originalLength = this.clientConfig.promptProfileOrder.order.length;
    this.clientConfig.promptProfileOrder.order = this.clientConfig.promptProfileOrder.order.filter(
      (id: string) => databaseIds.includes(id)
    );
    if (originalLength !== this.clientConfig.promptProfileOrder.order.length) {
      reSave = true;
    }
    if (reSave) {
      this.clientConfigService.save(this.clientConfig).subscribe({
        next: (clientConfig) => {
          console.log(
            'Mismatch between promptProfileOrder and prompts in dynamo db corrected automatically.'
          );
        },
      });
    }
  }

  private populateDatasource(
    data: any[],
    dataSource: MatTableDataSource<IPromptProfile>,
    sort: MatSort
  ): void {
    console.log('populating with', data);
    dataSource.data = data;
    dataSource.sort = sort;
    dataSource.sortingDataAccessor = (data: any, sortHeaderId) => {
      if (sortHeaderId === 'entityType') {
        return this.getEntityLabel(data.entityType).toUpperCase();
      }
      const value = data[sortHeaderId];
      return typeof value === 'string' ? value.toUpperCase() : value;
    };
  }

  private populateClientPromptsDatasource(data: any[]): void {
    const sortedData = this.sortProfileOrder(data as IPromptProfile[]);
    this.populateDatasource(sortedData, this.clientPromptsDataSource, this.clientPromptsTableSort);
    this.countPromptFilters(data);
    if (this.currentEntityFilter) {
      this.filterEntities('entityType', this.currentEntityFilter);
    } else if (this.searchFilter) {
      this.filterData(this.searchFilter);
    }
  }

  private populateKyloePromptsDatasource(data: any[]): void {
    this.populateDatasource(data, this.kyloePromptsDataSource, this.kyloePromptsTableSort);
  }

  private formatPromptForDynamo(
    prompt: IPromptProfile,
    hasEntityChanged?: boolean
  ): IPromptProfile {
    prompt.canCustomize = true;
    if (!prompt.promptId || hasEntityChanged) {
      prompt.promptId = prompt.entityType + '-' + crypto.randomUUID();
    }
    return Object.assign(prompt, this.promptProfileService.encodePromptFields(prompt));
  }

  protected getIntegrationValue(input: string): string {
    return this.integrationMappings[input];
  }

  filterDataEvent(event: any) {
    this.filterData((event.target as HTMLInputElement).value);
  }

  filterData(filterValue: string) {
    this.setFilterDataPredicate(this.clientPromptsDataSource, filterValue);
    this.setFilterDataPredicate(this.kyloePromptsDataSource, filterValue);

    this.noResults = this.clientPromptsDataSource.filteredData.length === 0;
    this.noKSPResults = this.kyloePromptsDataSource.filteredData.length === 0;
    this.currentEntityFilter = undefined;
  }

  protected filterEntities(column: string, filterValue: BullhornEntityType): void {
    this.clearInput(this.searchInput.nativeElement);
    this.setFilterEntitiesPredicate(this.clientPromptsDataSource, column, filterValue);
    this.setFilterEntitiesPredicate(this.kyloePromptsDataSource, column, filterValue);

    this.currentEntityFilter = filterValue;
  }

  protected setFilterDataPredicate(
    MatTableDataSource: MatTableDataSource<IPromptProfile>,
    filterValue: string
  ): void {
    MatTableDataSource.filterPredicate = (data: any, filter: string) => {
      const entityType = data['entityType'].toString().toLowerCase().includes(filter);
      const displayName = data['displayName'].toString().toLowerCase().includes(filter);
      const action = data['action'].toString().toLowerCase().includes(filter);
      return entityType || displayName || action;
    };
    this.searchFilter = filterValue.trim().toLowerCase();
    MatTableDataSource.filter = this.searchFilter;
  }

  protected setFilterEntitiesPredicate(
    MatTableDataSource: MatTableDataSource<IPromptProfile>,
    column: string,
    filterValue: string
  ) {
    MatTableDataSource.filterPredicate = (data: any, filter: string) => {
      const value = data[column].toString().toLowerCase();
      return value.includes(filter.toLowerCase());
    };
    MatTableDataSource.filter = filterValue ? filterValue.toString() : '';
  }

  protected copyToClipboard(aString: string): void {
    this.clipboard.copy(aString);
    this.messageNotificationService.success(`Copied ${aString} to clipboard`);
  }

  private countPromptFilters(data: any[]) {
    this.entityTypesUsed = [];
    for (const prompt of data) {
      const { entityType } = prompt;
      const existingCount = this.entityTypesUsed.find((c) => c.entityType === entityType);

      if (existingCount) {
        existingCount.count++;
      } else {
        this.entityTypesUsed.push({ entityType, count: 1 });
      }
    }
    this.entityTypesUsed.sort((a, b) => a.entityType.localeCompare(b.entityType));
  }

  private moveToTop(promptOrder: string[], prompt: IPromptProfile): string[] {
    const index = promptOrder.indexOf(prompt.promptId!);
    if (index > 0) {
      promptOrder.splice(index, 1);
    }
    promptOrder.unshift(prompt.promptId!);
    return promptOrder;
  }

  private addPromptToOrder(
    promptOrder: string[],
    prompt: IPromptProfile,
    existingPrompt: IPromptProfile
  ): string[] {
    const index = promptOrder.indexOf(existingPrompt.promptId!);
    if (index >= 0) {
      promptOrder.splice(index + 1, 0, prompt.promptId!);
    }
    return promptOrder;
  }

  private getEntityMeta() {
    return JSON.parse(localStorage.getItem(this.entityKey) as string);
  }

  public getDisplayedColumns() {
    const columns = this.clientTableDisplayedColumns;
    if (columns.length >= this.MAX_COLUMNS && !this.DTAIEnabled && !this.ADAIEnabled) {
      return columns.splice(3, 1);
    } else if (columns.length < this.MAX_COLUMNS && (this.DTAIEnabled || this.ADAIEnabled)) {
      return columns.splice(3, 0, 'integration');
    } else {
      return columns;
    }
  }

  public getDisplayedKyloeColumns() {
    return this.kyloeTableDisplayedColumns;
  }
  // Keyboard Shortcuts - Use Shift + Ctrl + F to Search and Esc to clear
  // - Use Alt + Shift + V to enlarge version number
  @HostListener('document:keydown', ['$event'])
  protected handleKeydownEvent(event: KeyboardEvent) {
    const searchInput = this.searchInput?.nativeElement;
    const clearSearch = this.clearSearch?.nativeElement;

    if (event.code === 'Escape' && this.clearSearch && searchInput === document.activeElement) {clearSearch.click();}

    if (event.ctrlKey && event.altKey && event.code === 'KeyC') { this.showAdminDialog(); }

    if (this.dialog.openDialogs.length === 0 && event.ctrlKey && event.shiftKey) {
      if (event.code === 'KeyF') {
        searchInput.focus();
      }
    }

    if (event.altKey && event.shiftKey) {
      if (event.code === 'KeyV') {
        this.expandVersion = true;
        setTimeout(() => {
          this.expandVersion = false;
        }, 3000);
      }
    }
  }
}

export interface IModalCloseEventData {
  action: 'save' | 'delete';
  promptProfile: IPromptProfile;
}

interface ISavePromptArgs {
  prompt: IPromptProfile;
  copyOfPrompt?: IPromptProfile;
  hasEntityChanged?: boolean;
}
